import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { LocationSwitcher } from "src/components/LocationSwitcher";
import { useDealers } from "src/context/DealersContext";
import { useDealer } from "src/hooks/useDealer";
import {
  LoadingContainer,
  ModalContainer,
  ModalOverlay,
} from "./LocationSwitcherModal.styles";

type LocationSwitcherModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const LocationSwitcherModal = ({
  isOpen,
  onClose,
}: LocationSwitcherModalProps) => {
  const navigate = useNavigate();
  const { dealer_id } = useDealer();
  const { dealerOptions, isLoading } = useDealers();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const handleLocationSelect = (selectedDealerId: string) => {
    if (selectedDealerId === "porchpass") {
      void navigate("/porchpass/dealers");
    } else {
      void navigate(`/${selectedDealerId}/dashboard`, { replace: true });
    }
    handleClose();
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = "hidden";

      const animationTimeout = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => {
        clearTimeout(animationTimeout);
        document.body.style.overflow = "unset";
      };
    } else {
      document.body.style.overflow = "unset";
      setIsAnimating(false);
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return createPortal(
    <ModalOverlay onClick={handleClose} isAnimating={isAnimating}>
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        isAnimating={isAnimating}
      >
        {isLoading ? (
          <LoadingContainer>Loading...</LoadingContainer>
        ) : dealerOptions.length === 0 ? (
          <LoadingContainer>No locations available.</LoadingContainer>
        ) : (
          <LocationSwitcher
            dealerOptions={dealerOptions}
            onLocationSelect={handleLocationSelect}
            selectedDealerId={dealer_id}
            showCloseButton={true}
            onClose={handleClose}
          />
        )}
      </ModalContainer>
    </ModalOverlay>,
    document.body,
  );
};
